

import {defineComponent, ref, onMounted, onBeforeMounted, watch} from "vue";
import PriceGroupItem from "../../components/items/PriceGroupItem.vue";
import {useTicketStore} from '~vue/stores/TicketStore.ts';
import {storeToRefs} from 'pinia';
import {translate} from '@elements/translations';
import {fetcher, assertData} from "~vue/utils/utils.ts";
import {getPriceUrl, getServiceUrl, getPriceGroupUrl} from "~vue/utils/urls.ts";
import {ServicesStruct, PriceGroupStruct, PriceStruct} from "~vue/utils/structs.ts";
import Popper from "vue3-popper";
import Error from "~vue/components/error/Error.vue";

export default defineComponent({
    components: {
        PriceGroupItem,
        Popper,
        Error
    },
    setup(props) {
        const ticketStore = useTicketStore();
        const {
            selectedDate,
            ticketPriceGroups,
            ticketPrices,
            ticketCounter,
            selectedTicketType,
            selectedCatalogId,
            availableTickets,
            selectedTicketSubType,
            ticketConfigurations,
            ticketServices,
            hasServices,
            type
        } = storeToRefs(ticketStore);
        const {changeValidation, changeTicketCounter, changeTicketPrices, changeTicketPriceGroups, changeTicketConfigurations, changeTicketServices} = ticketStore;
        const ticketCounterSum = ref(0);

        const loadingServices = ref({
            isLoading: false,
            error: false,
            errorMessage: ''
        });

        const loadingPriceGroups = ref({
            isLoading: false,
            error: false,
            errorMessage: ''
        });

        const loadingPrice = ref({
            isLoading: false,
            error: false,
            errorMessage: ''
        });

        function getMaxCounter(currentCounter: number) {
            if (availableTickets.value) {
                return availableTickets.value - ticketCounterSum.value + currentCounter;
            } else {
                return 30;
            }
        }

        onMounted(() => {
            if(ticketPriceGroups.value.length === 0) {
                fetchPriceGroups();
            }

            if(Object.keys(ticketPrices.value).length === 0) {
                fetchPrices();
            }

            if (Object.keys(ticketCounter.value).length === 0) {
                ticketPriceGroups.value.forEach((priceGroup) => {
                    changeTicketCounter(priceGroup.id, 0);
                });
            }

            fetchServices();

            watch(ticketCounter.value, (value) => {
                // get sum of all tickets
                ticketCounterSum.value = 0;
                Object.keys(value).forEach((key) => {
                    ticketCounterSum.value += value[key];
                });

                if (ticketCounterSum.value > 0) {
                    changeValidation('persons', true);
                } else {
                    changeValidation('persons', false);
                }
                getConfigurations();

            });
        });

        const fetchPrices = async () => {
            loadingPrice.value.isLoading = true;
            let url = getPriceUrl(type.value,`/${selectedCatalogId.value}/${selectedTicketSubType.value}`)
            await fetcher(url).then((result) => {
                if(result.success) {
                    assertData(result, PriceStruct, url, 'fetchPriceForDate', 'danger');
                    changeTicketPrices(result.price.priceByPriceGroup);
                } else {
                    loadingPrice.value.error = true;
                    loadingPrice.value.errorMessage = result.errorMessage;
                }
                loadingPrice.value.isLoading = false;
            }).catch((error) => {
                console.warn(error);
                loadingPrice.value.isLoading = false;
                loadingPrice.value.error = true;
            });
        }

        const fetchPriceGroups = async () => {
            loadingPriceGroups.value.isLoading = true;
            let url = getPriceGroupUrl(type.value,`/${selectedCatalogId.value}/${selectedTicketSubType.value}`)
            await fetcher(url).then((result) => {
                if(result.success) {
                    assertData(result, PriceGroupStruct, url, 'fetchPriceGroups', 'danger');
                    loadingPriceGroups.value.error = false;
                    loadingPriceGroups.value.errorMessage = '';

                    //prefill
                    if(ticketPriceGroups.value.length === 0) {
                        changeTicketPriceGroups(result.priceGroups);
                    }
                } else {
                    loadingPriceGroups.value.error = true;
                    loadingPriceGroups.value.errorMessage = result.errorMessage;
                }
                loadingPriceGroups.value.isLoading = true;
            }).catch((error) => {
                console.warn(error);
                loadingPriceGroups.value.isLoading = false;
                loadingPriceGroups.value.error = true;
            });
        }

        const fetchServices = async () => {
            loadingServices.value.isLoading = true;
            let url = getServiceUrl(type.value,`/${selectedCatalogId.value}/${selectedTicketSubType.value}` + (selectedDate.value.start ? `/${selectedDate.value.start}` : ''))
            await fetcher(url).then((result) => {
                if(result.success) {
                    assertData(result, ServicesStruct, url, 'fetchTicketTypes', 'danger');
                    loadingServices.value.error = false;
                    loadingServices.value.errorMessage = '';

                    if(result.services) {
                        changeTicketServices(result.services);
                    }
                } else {
                    loadingServices.value.error = true;
                    loadingServices.value.errorMessage = result.errorMessage;
                }
                loadingServices.value.isLoading = false;
            }).catch((error) => {
                loadingServices.value.error = true;
                loadingServices.value.isLoading = true;
            });
        }

        const getConfigurations = () => {
            let configurations = ticketConfigurations.value;

            Object.keys(ticketCounter.value).forEach((key) => {
                let priceGroup = ticketPriceGroups.value.find((priceGroup) => priceGroup.id === key);
                for (let i = 0; i < ticketCounter.value[key]; i++) {
                    if (!configurations.find((configuration) => configuration.key === i + "-" + priceGroup?.id)) {
                        configurations.push({
                            key: i + "-" + priceGroup?.id,
                            index: i + 1,
                            priceGroupId: priceGroup?.id,
                            priceGroupName: {
                                name: priceGroup?.name,
                                singularName: priceGroup?.singularName,
                                pluralName: priceGroup?.pluralName
                            },
                            price: ticketPrices.value[key].price,
                            priceSum: ticketPrices.value[key].price,
                            ...(ticketServices.value.length === 0 && {priceSum: ticketPrices.value[key].price}),
                            ...(ticketServices.value.length > 0 && {priceSum: ticketPrices.value[key].price + ticketServices.value[0]?.priceByPriceGroup[key].price}),
                            ...(ticketServices.value.length > 0 && {service: ticketServices.value[0]?.id}),
                            ...(ticketServices.value.length > 0 && {serviceName: ticketServices.value[0]?.name}),
                        });
                    } else {
                        configurations = configurations.filter((configuration) => configuration.key !== ticketCounter.value[key] + "-" + priceGroup?.id);
                    }
                }

                if (ticketCounter.value[key] === 0) {
                    configurations = configurations.filter((configuration) => configuration.key !== 0 + "-" + priceGroup?.id);
                }
            });

            changeTicketConfigurations(configurations);
            return configurations;
        }

        const onChangeTicketServices = (item) => {
            // get configuration form store with key item.key
            let configurations = ticketConfigurations.value;
            let configuration = configurations.find((configuration) => configuration.key === item.key);
            // change existing configuration with new service
            configuration.service = item.service;
            configuration.serviceName = ticketServices.value.find((service) => service.id === item.service).name;
            // get price from service and add to price from ticket
            configuration.priceSum = ticketPrices.value[item.priceGroupId].price
                + ticketServices.value.find((service) => service.id === item.service).priceByPriceGroup[item.priceGroupId].price;

            // change configuration in store
            changeTicketConfigurations(configurations);
        }

        return {
            ticketPriceGroups,
            onChangeTicketServices,
            ticketConfigurations,
            ticketPrices,
            changeTicketCounter,
            hasServices,
            ticketCounter,
            ticketCounterSum,
            ticketServices,
            loadingServices,
            loadingPriceGroups,
            availableTickets,
            loadingPrice,
            getMaxCounter,
            translate
        };
    }
})
